import styled from 'styled-components';

// Shared palette for the session clock / strip.
export const CLOCK_COLORS = {
  active: '#52c41a', // active learning (progress-report time)
  idle: '#bfbfbf', // in-station, not actively learning
  track: '#f0f0f0', // empty time (lobby / gaps) — "nothing happening"
  lobby: '#e0e0e0',
  leBands: ['#5b8def', '#9254de', '#13c2c2', '#fa8c16', '#eb2f96', '#2f54eb'],
  correct: '#1677ff', // distinct from active-learning green
  incorrect: '#ff4d4f',
  unknownAnswer: '#8c8c8c',
  text: '#262626',
  subtext: '#8c8c8c',
};

export const ClockWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: center;
`;

export const ClockColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 auto;
`;

export const StatsColumn = styled.div`
  flex: 1 1 280px;
  min-width: 260px;
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`;

export const StatCard = styled.div`
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 10px 12px;
`;

export const StatValue = styled.div<{ $color?: string }>`
  font-size: 18px;
  font-weight: 600;
  color: ${({ $color }): string => $color ?? CLOCK_COLORS.text};
  line-height: 1.2;
`;

export const StatLabel = styled.div`
  font-size: 12px;
  color: ${CLOCK_COLORS.subtext};
  margin-top: 2px;
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 12px;
  font-size: 12px;
  color: ${CLOCK_COLORS.subtext};
`;

export const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const Swatch = styled.span<{ $color: string }>`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${({ $color }): string => $color};
`;

export const CenterTotal = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${CLOCK_COLORS.text};
`;

export const CenterSub = styled.div`
  font-size: 12px;
  color: ${CLOCK_COLORS.subtext};
`;

// ── Linear strip ─────────────────────────────────────────────────────────────
export const StripWrap = styled.div`
  position: relative;
  width: 100%;
  margin-top: 16px;
`;

// Video-timeline ruler above the bar.
export const StripRuler = styled.div`
  position: relative;
  width: 100%;
  height: 15px;
  margin-bottom: 3px;
`;

export const StripTickLabel = styled.div`
  position: absolute;
  top: 0;
  font-size: 11px;
  color: ${CLOCK_COLORS.subtext};
  white-space: nowrap;
`;

export const StripTrack = styled.div`
  position: relative;
  width: 100%;
  height: 30px;
  background: ${CLOCK_COLORS.track};
  border-radius: 5px;
  overflow: hidden;
  cursor: crosshair;
`;

export const StripGridline = styled.div<{ $left: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }): string => `${$left}%`};
  width: 1px;
  background: rgba(0, 0, 0, 0.06);
  pointer-events: none;
  z-index: 1;
`;

export const StripPlayhead = styled.div<{ $left: number }>`
  position: absolute;
  top: -4px;
  bottom: -4px;
  left: ${({ $left }): string => `${$left}%`};
  width: 2px;
  background: #262626;
  pointer-events: none;
  z-index: 4;
`;

export const StripBlock = styled.div<{ $left: number; $width: number; $bg: string }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }): string => `${$left}%`};
  width: ${({ $width }): string => `${$width}%`};
  background: ${({ $bg }): string => $bg};
  border-right: 1px solid #ffffff;
  box-sizing: border-box;
`;

export const StripActive = styled.div<{ $width: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: ${({ $width }): string => `${$width}%`};
  background: ${CLOCK_COLORS.active};
`;

export const StripLeRow = styled.div`
  position: relative;
  width: 100%;
  height: 10px;
  margin-top: 4px;
`;

export const StripLeBand = styled.div<{ $left: number; $width: number; $bg: string }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }): string => `${$left}%`};
  width: ${({ $width }): string => `${$width}%`};
  background: ${({ $bg }): string => $bg};
  border-radius: 3px;
  opacity: 0.85;
`;

export const StripAnswerTick = styled.div<{ $left: number; $bg: string }>`
  position: absolute;
  top: -3px;
  left: ${({ $left }): string => `${$left}%`};
  width: 8px;
  height: 8px;
  margin-left: -4px;
  border-radius: 50%;
  background: ${({ $bg }): string => $bg};
  border: 1px solid #ffffff;
  z-index: 2;
`;

export const StripAxis = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: ${CLOCK_COLORS.subtext};
  margin-top: 4px;
`;

export const EmptyNote = styled.div`
  padding: 16px;
  color: ${CLOCK_COLORS.subtext};
  font-size: 13px;
  background: #fafafa;
  border: 1px dashed #e0e0e0;
  border-radius: 8px;
`;
